import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, computed } from "vue";
  import CustomizeHouseSlider from "@/components/builder/CustomizeHouseSlider.vue";
  import store from "@/store";
  import { formatPrice } from "@/helpers/transformation/price";
  import BuilderLoanCalculator from "@/components/builder/BuilderLoanCalculator.vue";
  import { useToast } from "vue-toastification";
  import router from "@/router";
  import InitialHoldModal from "@/components/ui/modals/InitialHoldModal.vue";
  import InitialHoldWithPartnerModal from "@/components/ui/modals/InitialHoldWithPartnerModal.vue";
  import SuccessInitialHoldModal from "@/components/ui/modals/SuccessInitialHoldModal.vue";
  import BuilderHeader from "@/components/builder/BuilderHeader.vue";
  import { changePrimaryColor } from "@/helpers/customer/changePrimaryColor";
  import { EstateInterface } from "@/types/EstateInterface";
  import HouseSelectionsCard from "@/components/builder/HouseSelectionsCard.vue";
  import HouseFeatures from "@/components/builder/HouseFeatures.vue";
  import { CustomizeHouseStepsReviewQuoteEnum } from "@/enums/CustomizeHouseSteps";
  import Stepper from "@/components/ui/Stepper.vue";
  import { LotInterface } from "@/types/LotInterface";
  import { BuilderQuote } from "@/types/BuilderQuote";
  import { BuilderQuoteStatus } from "@/enums/BuilderQuoteStatus";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { formatPrice as formatMeters } from "@/helpers/transformation/price";
  import BuilderOpenMessageReviewQuote from "@/components/builder/BuilderOpenMessageReviewQuote.vue";
  import { BuilderMessageType } from "@/enums/BuilderMessageType";
  import { scrollToNextStepMobile } from "@/helpers/builder/scrollToNextStep";
  import { StepTypeEnum } from "@/enums/BuilderStepType";
  import BuilderFloorplansFeaturesModal from "@/components/builder/BuilderFloorplansFeaturesModal.vue";
  import { BuilderStepsIds } from "@/enums/BuilderStepsIds";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'RequestQuoteView',
  setup(__props, { expose: __expose }) {
  __expose();

  const { isDesktop } = useIsDesktop(990);
  const quote = ref<BuilderQuote | null>();
  const sliderImages = ref<string[]>([]);
  const lot = ref<LotInterface>();
  const estate = ref<EstateInterface | null>(null);
  const loading = ref(true);
  const house = ref<HouseDesignInterface>();

  const isOpenedInitialHoldModal = ref(false);
  const isOpenedInitialHoldModalWithPartner = ref(false);
  const successfulInitialRequestOpened = ref(false);
  const isBuildPrice = ref(false);
  const fadeInEffect = ref(false);
  const fadeInEffectSlider = ref(true);
  const isButtonClicked = ref(false);

  const openBuildPrice = () => {
    isBuildPrice.value = !isBuildPrice.value;

    setTimeout(() => {
      fadeInEffect.value = !fadeInEffect.value;
    }, 50);
  };

  const lotForModal = computed(() => ({
    lotId: {
      ...lot.value,
      estateId: estate.value?._id,
    },
  }));

  const getSiteCostForHouse = (
    lot: LotInterface,
    house: HouseDesignInterface
  ) => {
    if (!lot || !house) {
      return 0;
    }

    const builder = lot.builders.find(
      (builder) => builder && builder.builderId === house.builderId
    );

    if (!builder) {
      return 0;
    }

    const houseIdInLot = builder.housesIds.find(
      (houseId: { houseId: string }) => houseId && houseId.houseId === house._id
    );

    return houseIdInLot ? houseIdInLot.siteCosts || 0 : 0;
  };

  const packagePrice = computed(() => {
    if (!quote.value || !lot.value || !house.value) {
      return 0;
    }

    const lotPrice = quote.value.selections.lot.price;
    const housePrice = quote.value.selections.house.price;
    const siteCost = getSiteCostForHouse(lot.value, house.value);

    return lotPrice + housePrice + siteCost;
  });

  const houseId = computed(() => {
    if (!quote.value) {
      return;
    }

    return quote.value.selections.house.houseId;
  });

  const toHouseAndFeatures = () => {
    scrollToNextStepMobile(BuilderStepsIds.HOUSE);
  };

  const onButtonClickSlider = () => {
    fadeInEffectSlider.value = !fadeInEffectSlider.value;
    isButtonClicked.value = !isButtonClicked.value;

    if (!isButtonClicked.value) {
      toHouseAndFeatures();
    }
  };

  const getEstateAndSetPrimaryColor = (estateId: string) => {
    loading.value = true;
    store
      .dispatch("estate/getEstate", estateId)
      .then(({ data }) => {
        changePrimaryColor(data.iconColor);
        estate.value = data;
      })
      .finally(() => {
        loading.value = false;
      });
  };
  const getSliderImages = () => {
    if (!quote.value) return;

    sliderImages.value = [quote.value.selections.facade.url];
  };

  const reserveLot = () => {
    if (!quote.value) return;

    const estateId = quote.value.estateId;
    const route = router.resolve({
      name: "EOIRequest",
      params: {
        estateId,
        lotId: lot.value?._id,
      },
      query: {
        stageId: lot.value?.stageId._id,
      },
    });

    window.open(route.href);
  };
  const onSecureLot = () => {
    if (!estate.value?.showInitialHold) {
      reserveLot();
      return;
    }

    isOpenedInitialHoldModal.value = true;
  };
  const onCloseInitialHoldModal = () => {
    isOpenedInitialHoldModal.value = false;
  };
  const onOpenPartnerModal = () => {
    isOpenedInitialHoldModal.value = false;
    isOpenedInitialHoldModalWithPartner.value = true;
  };
  const backToSelectHoldType = () => {
    isOpenedInitialHoldModal.value = true;
    isOpenedInitialHoldModalWithPartner.value = false;
  };
  const onSendHoldRequest = () => {
    isOpenedInitialHoldModal.value = false;
    successfulInitialRequestOpened.value = true;
  };

  const getQuote = async () => {
    try {
      loading.value = true;

      const { data } = await store.dispatch(
        "builder/getQuote",
        router.currentRoute.value.params.quoteId
      );

      quote.value = data;

      getEstateAndSetPrimaryColor(data.estateId);
      getLot(data.selections.lot.lotId);
      getSliderImages();
      getHouse();
    } catch (error) {
      router.push({ name: "Dashboard" });
    } finally {
      loading.value = false;
    }
  };

  const getLot = (lotId: string) => {
    loading.value = true;
    store
      .dispatch("lot/getOpenLot", lotId)
      .then((res) => {
        lot.value = res.data;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getHouse = () => {
    if (houseId.value) {
      loading.value = true;
      store
        .dispatch("builder/getHouseById", houseId.value)
        .then((res) => {
          house.value = res.data;
        })
        .finally(() => {
          loading.value = false;
        });
    }
  };

  onMounted(() => {
    getQuote();
  });

const __returned__ = { isDesktop, quote, sliderImages, lot, estate, loading, house, isOpenedInitialHoldModal, isOpenedInitialHoldModalWithPartner, successfulInitialRequestOpened, isBuildPrice, fadeInEffect, fadeInEffectSlider, isButtonClicked, openBuildPrice, lotForModal, getSiteCostForHouse, packagePrice, houseId, toHouseAndFeatures, onButtonClickSlider, getEstateAndSetPrimaryColor, getSliderImages, reserveLot, onSecureLot, onCloseInitialHoldModal, onOpenPartnerModal, backToSelectHoldType, onSendHoldRequest, getQuote, getLot, getHouse, ref, onMounted, computed, CustomizeHouseSlider, get store() { return store }, get formatPrice() { return formatPrice }, BuilderLoanCalculator, get useToast() { return useToast }, get router() { return router }, InitialHoldModal, InitialHoldWithPartnerModal, SuccessInitialHoldModal, BuilderHeader, get changePrimaryColor() { return changePrimaryColor }, get EstateInterface() { return EstateInterface }, HouseSelectionsCard, HouseFeatures, get CustomizeHouseStepsReviewQuoteEnum() { return CustomizeHouseStepsReviewQuoteEnum }, Stepper, get LotInterface() { return LotInterface }, get BuilderQuote() { return BuilderQuote }, get BuilderQuoteStatus() { return BuilderQuoteStatus }, get useIsDesktop() { return useIsDesktop }, get HouseDesignInterface() { return HouseDesignInterface }, get formatMeters() { return formatMeters }, BuilderOpenMessageReviewQuote, get BuilderMessageType() { return BuilderMessageType }, get scrollToNextStepMobile() { return scrollToNextStepMobile }, get StepTypeEnum() { return StepTypeEnum }, BuilderFloorplansFeaturesModal, get BuilderStepsIds() { return BuilderStepsIds } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})